/**
 * Document ID Utilities
 * 
 * Centralized validation and utilities for document IDs.
 * Doc IDs follow the format: doc_<id>
 * 
 * Examples:
 * - doc_ml5j07rw_tycjv (with underscore separator)
 * - doc_abc123xyz (continuous)
 * - doc_V1StGXR8_Z (10 chars from nanoid)
 */

/**
 * Canonical regex for validating document IDs.
 * Matches: doc_ followed by alphanumeric characters (base62: A-Z, a-z, 0-9, and underscores within)
 * 
 * Pattern: doc_[A-Za-z0-9_-]+
 * - Accepts uppercase and lowercase letters (base62 from nanoid)
 * - Minimum 5 chars total (doc_ + 1 char)
 * - Allows underscores within the ID part
 */
export const DOC_ID_REGEX = /^doc_[A-Za-z0-9_-]+$/;

/**
 * Validate a document ID against the canonical pattern.
 * 
 * @param docId - The document ID to validate
 * @returns true if valid, false otherwise
 */
export function isValidDocId(docId: string | null | undefined): docId is string {
  if (!docId || typeof docId !== 'string') {
    return false;
  }
  return DOC_ID_REGEX.test(docId);
}

/**
 * Assert that a document ID is valid, throwing an error if not.
 * Use this for defensive programming before critical operations.
 * 
 * @param docId - The document ID to validate
 * @throws Error if docId is invalid
 */
export function assertValidDocId(docId: string | null | undefined): asserts docId is string {
  if (!isValidDocId(docId)) {
    throw new Error(`Invalid document ID format: ${docId}. Expected format: doc_<id> (letters, numbers, _ or -)`);
  }
}

/**
 * Sanitize a potential document ID by extracting just the docId portion
 * if it's embedded in a URL or path.
 * 
 * @param input - Input that may contain a docId (URL, path, or raw docId)
 * @returns The extracted docId, or null if none found
 */
export function extractDocId(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  // Try to match the pattern anywhere in the input
  const match = input.match(/doc_[A-Za-z0-9_-]+/);
  return match ? match[0] : null;
}

/**
 * Sanitize a raw docId string from user input, URL params, or QR scans.
 * Trims leading/trailing whitespace and strips trailing punctuation characters
 * (.,;:)]} and -) that can appear from copy-paste or PDF rendering artefacts.
 *
 * @param input - Raw string that should be a docId
 * @returns The sanitized docId if it passes validation, or null
 */
export function sanitizeDocId(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  // Step 1: Trim surrounding whitespace
  let sanitized = input.trim();

  // Step 2: Strip trailing punctuation/symbols that don't belong in a docId.
  // Characters stripped: . , ; : ) ] } and - (trailing dash common PDF artefact)
  sanitized = sanitized.replace(/[.,;:)\]}\-]+$/, '');

  // Step 3: Validate the cleaned string
  if (!DOC_ID_REGEX.test(sanitized)) {
    return null;
  }

  return sanitized;
}
